import React, { useEffect, useState } from 'react';
import './App.css';
import api from './http';
import { Link } from 'react-router-dom';
import ManualEntryWizard from './admin/wizards/ManualEntryWizard';
import StationEntryForm from './admin/wizards/StationEntryForm';

function ManualEntry() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  const [companies, setCompanies] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [alert, setAlert] = useState(null);

  // Fetch active events on mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch data when event is selected
  useEffect(() => {
    if (selectedEventId) {
      fetchEventData(selectedEventId);
    } else {
      setCompanies([]);
      setStations([]);
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const res = await api.get('/api/test-events?status=active');
      const activeEvents = Array.isArray(res.data) ? res.data : [];
      setEvents(activeEvents);

      // Auto-select if only one event
      if (activeEvents.length === 1) {
        setSelectedEventId(activeEvents[0].id);
      } else if (activeEvents.length > 0) {
        // Or select the first one by default
        setSelectedEventId(activeEvents[0].id);
      }
    } catch (err) {
      console.error('Failed to load events', err);
      setError('Failed to load active test events.');
    }
  };

  const fetchEventData = async (eventId) => {
    setLoading(true);
    try {
      const [companiesRes, stationsRes] = await Promise.all([
        api.get(`/api/test-events/${eventId}/companies?withDetails=1`), // Request details to see candidates/programs
        api.get(`/api/test-events/${eventId}/stations`)
      ]);
      setCompanies(companiesRes.data);
      setStations(stationsRes.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load data', err);
      setError('Failed to load data for the selected event.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminCompleteTest = async (stationId, result) => {
    try {
      await api.post('/api/admin/complete-test', { station_id: stationId, result });
      setAlert({ type: 'success', message: `Station marked as ${result}!` });
      fetchEventData(selectedEventId);
    } catch (err) {
      console.error('Failed to complete test', err);
      setAlert({
        type: 'danger',
        message: 'Failed to complete test: ' + (err.response?.data?.error || err.message)
      });
    }
  };

  const onWizardComplete = () => {
    setAlert({ type: 'success', message: 'Company successfully added!' });
    fetchEventData(selectedEventId);
  };

  return (
    <div className="dashboard-root">
      <div className="header">
        <h1>Manual Data Entry</h1>
        <div style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/admin" className="btn" style={{ textDecoration: 'none', fontWeight: 600 }}>
            Back to Admin Hub
          </Link>
          <Link to="/dashboard" className="btn" style={{ textDecoration: 'none', fontWeight: 600 }}>
            Testing Dashboard
          </Link>
        </div>
      </div>

      <div className="main-content" style={{ marginBottom: 20 }}>
        <section className="section" style={{ borderLeft: '4px solid #3182ce' }}>
          <h2>1. Select Test Event</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              style={{ padding: '8px 12px', fontSize: '16px', borderRadius: 4, minWidth: 250 }}
            >
              <option value="">-- Select Event --</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} ({new Date(e.event_date).toLocaleDateString()})
                </option>
              ))}
            </select>
            {events.length === 0 && !loading && (
              <span style={{ color: '#e53e3e' }}>
                No active events found. Please create/activate an event first.
              </span>
            )}
          </div>
        </section>
      </div>

      {loading && <div className="alert info">Loading data...</div>}
      {error && <div className="alert danger">{error}</div>}
      {alert && (
        <div className={`alert ${alert.type}`}>
          {alert.message}
          <button
            onClick={() => setAlert(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            &times;
          </button>
        </div>
      )}

      {selectedEventId && (
        <>
          <div className="main-content">
            {/* Embedded Forms Area */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', marginBottom: '2rem' }}>
              {/* Add Company Form */}
              <div style={{ flex: '2', minWidth: '400px' }}>
                <ManualEntryWizard
                  eventId={selectedEventId}
                  initialCompanyNumber={
                    companies.reduce((max, c) => {
                      const num = parseInt(c.name, 10);
                      return !isNaN(num) && num > max ? num : max;
                    }, 0) + 1
                  }
                  onComplete={onWizardComplete}
                />
              </div>

              {/* Add Station Form */}
              <div style={{ flex: '1', minWidth: '350px' }}>
                <StationEntryForm
                  eventId={selectedEventId}
                  onStationAdded={() => fetchEventData(selectedEventId)}
                />
              </div>
            </div>
          </div>

          <div className="main-content">
            <section className="section">
              <h2>Companies ({companies.length})</h2>
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Company #</th>
                    <th>Candidates</th>
                    <th>Program</th>
                    <th>Stats (C/P/F)</th>
                    <th>Current Station</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', color: '#888' }}>
                        No companies added yet.
                      </td>
                    </tr>
                  )}
                  {companies.map((company) => (
                    <tr key={company.id}>
                      <td>{company.name}</td>
                      <td>
                        {/* Display candidates with program info if different */}
                        {company.candidates && company.candidates.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {company.candidates.map((c) => (
                              <span key={c.id}>
                                {c.first_name} {c.last_name}
                                {c.program_name && c.program_name !== company.program_name && (
                                  <span
                                    style={{
                                      fontSize: '0.8em',
                                      color: '#805ad5',
                                      marginLeft: '6px'
                                    }}
                                  >
                                    ({c.program_name})
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#ccc' }}>No candidates</span>
                        )}
                      </td>
                      <td>{company.program_name}</td>
                      <td>
                        {company.completed_station_count || company.stations_completed || 0} /{' '}
                        {company.stations_passed || 0} / {company.stations_failed || 0}
                      </td>
                      <td>
                        {company.current_station_name
                          ? `#${company.current_station_number || '?'} - ${company.current_station_name}`
                          : 'Not assigned'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="section">
              <h2>Stations ({stations.length})</h2>
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Proctor</th>
                    <th>Status</th>
                    <th>Company</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stations.length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', color: '#888' }}>
                        No stations added yet.
                      </td>
                    </tr>
                  )}
                  {stations.map((station) => (
                    <tr
                      key={station.id}
                      className={station.current_company_id ? 'occupied' : 'available'}
                    >
                      <td>{station.station_number}</td>
                      <td>{station.name}</td>
                      <td>
                        {station.proctor_name || (
                          <span style={{ fontStyle: 'italic', color: '#999' }}>Unassigned</span>
                        )}
                      </td>
                      <td>
                        {station.current_company_id ? (
                          <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>Occupied</span>
                        ) : (
                          <span style={{ color: '#28a745', fontWeight: 'bold' }}>Available</span>
                        )}
                      </td>
                      <td>
                        {station.current_company_id ? `Co ${station.current_company_name}` : '-'}
                      </td>
                      {station.current_company_id && (
                        <td>
                          <button
                            className="btn pass"
                            onClick={() => handleAdminCompleteTest(station.id, 'passed')}
                          >
                            ✅ Pass
                          </button>
                          <button
                            className="btn fail"
                            onClick={() => handleAdminCompleteTest(station.id, 'failed')}
                          >
                            ❌ Fail
                          </button>
                        </td>
                      )}
                      {!station.current_company_id && <td>-</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

export default ManualEntry;
